import { AssistantService } from '@/api/services/assistant.service';
import { PLANS_TARGET } from '@/domain/assistant/assistant-nav-targets';
import { ASSISTANT_SCHEMA_VERSION } from '@/interfaces/assistant.interface';
import type {
  IAssistantAffectedGroup,
  IAssistantExcludedRow,
  IAssistantOpDto,
  IAssistantProposeResponse,
  IAssistantTooManyRowsDetails,
  TAssistantErrorCode,
  TAssistantKind,
  TAssistantOpName,
} from '@/interfaces/assistant.interface';
import type {
  IGhCalloutCard,
  IGhToolResultEnvelope,
  IHostToolDefinition,
} from '../gpthelp-host-contract';
import { refreshAssistantConfigOnRefusal } from '../assistant/assistant-config';
import { readAssistantError } from '../assistant/assistant-errors';
import {
  lockOf,
  lockToggleKey,
  permissionLockKey,
  planLockKey,
} from '../assistant/assistant-locks';
import type { TAssistantLock } from '../assistant/assistant-locks';
import {
  discardEmptyProposal,
  proposalCardInput,
} from '../assistant/assistant-proposal-context';
import { kindPageKey, refusalCard } from '../assistant/assistant-refusals';
import {
  affectedProductCount,
  affectedProductsCard,
} from '../cards/affected-cards';
import type { ICardFormat } from '../cards/card-text';
import { toolCardId } from '../cards/card-ids';
import { nothingToChangeCard } from '../cards/empty-proposal-card';
import { permissionLockCard, planLockCard } from '../cards/lock-cards';
import {
  hasRowsToApply,
  proposalCard,
  proposalMode,
} from '../cards/proposal-cards';
import type { IProposalCardInput } from '../cards/proposal-cards';
import { writesOffCard } from '../cards/result-cards';
import { rowEntity } from '../cards/row-presenters';
import {
  isNewChoiceExclusion,
  isNewProductExclusion,
  isNewSizeExclusion,
} from '../cards/row-values';
import type {
  IGpthelpTool,
  IGpthelpToolExecCtx,
  IGpthelpToolGate,
} from './tool-types';
import { canOpenPage } from './tool-display';
import { toolResultEnvelope } from './tool-envelope';
import { assistantCanToggle } from './tool-gate';
import { throwNormalizedApiError } from './tool-http';
import {
  fieldSchemasOf,
  MENU_OP_LIMITS,
  opsFromArgs,
} from './proposal-op-args';
import {
  kindOps,
  opsOfKind,
  PROPOSAL_KINDS,
  proposalToolAvailable,
} from './proposal-kinds';
import { buildScopeKey } from './tool-scope';

/** The request text sent with a proposal (plan §9.7). */
const REQUEST_TEXT_MAX = 280;
/** Exclusions the model reads at most. */
const EXCLUDED_MAX = 20;
/** Products and categories still hidden after the change that the model reads at most. */
const STILL_HIDDEN_MAX = 20;

/**
 * What the model reads for a lock or refusal card, which already says it
 * all (and what to do instead): opening a page would contradict it.
 */
const CARD_NOTE =
  'The card tells the user. Do not repeat it or open a page for it; one short sentence at most.';

/**
 * What the model reads for a lock card (the role, every branch, the plan or
 * the switch stops the change): the card note, and no other change in its
 * place unless the user asks for one (e.g. the products created visible when
 * the user asked for them hidden).
 */
const LOCK_NOTE = `${CARD_NOTE} Do not prepare a different change instead (for example without the part that cannot be done) unless the user asks for it.`;

/** Action API refusals that are locks: the role, every branch, the plan, the switch. */
const LOCK_CODES: readonly TAssistantErrorCode[] = [
  'ASSISTANT_FORBIDDEN',
  'ASSISTANT_PLAN_REQUIRED',
  'ASSISTANT_WRITES_OFF',
];

/**
 * What the model reads when a call prepared nothing (plan P0-6): the card
 * says why; the model says what to do instead.
 */
const EMPTY_NOTE =
  'Nothing was prepared; the card shows why and has no button. In one short sentence say what the user can do instead; do not open a page for it.';

/**
 * What the model reads with products a proposal turns on that stay hidden
 * (plan P0-3) while no category op is offered: what hides them is the
 * user's to change, never an offer.
 */
const STILL_HIDDEN_NOTE =
  'These products stay hidden for the reasons listed. Showing a category or a menu, or changing hours, is not a change you can prepare: never offer it; the user does it on the Menú page, which you can open for them.';

/**
 * What the model reads with what a proposal turns on that stays hidden
 * (plan P0-3, P2-4): the P0 text while no category op is offered; else the
 * fix of a category's own reason through the ops offered, as an offer only
 * (it shows every product of the category), and nothing else (plan F10
 * naming rule: an op is named only while it is offered). `ops`: the ops
 * offered that the member's role and plan let them use (`lockOf`), so a fix
 * they cannot make is never offered; `canHideEverywhere`: the member is over
 * every branch, the only one who can show a category that is off in every
 * branch (`categoryDisabled`; the Action API leaves it out otherwise).
 */
export function stillHiddenNote(
  ops: readonly string[],
  canHideEverywhere: boolean
): string {
  const availabilityReasons = canHideEverywhere
    ? 'categoryDisabled or categoryHiddenInBranch'
    : 'categoryHiddenInBranch';
  const fixes = [
    ops.includes('setCategoryAvailability')
      ? `${availabilityReasons} → setCategoryAvailability`
      : null,
    ops.includes('setCategoryPosOnly')
      ? 'categoryPosOnly → setCategoryPosOnly'
      : null,
  ].filter((fix): fix is string => fix !== null);
  if (fixes.length === 0) return STILL_HIDDEN_NOTE;
  return `These stay hidden for the reasons listed. You can prepare the fix for a category's own reason (${fixes.join(
    '; '
  )}), but it shows every product in that category: offer it, and prepare it only once the user agrees. Any other reason is not a change you can prepare: never offer it; the user changes it on the Menú page, which you can open for them.`;
}

/**
 * Why each product was left out, for the model only (plan P0-6); the why of
 * DISCOUNT_INVALID depends on the ops offered (`IDiscountTexts.invalidWhy`,
 * `invalidCreateWhy` for a new product).
 * Names no op or tool (plan F10).
 */
const EXCLUDED_WHY: Readonly<
  Record<Exclude<IAssistantExcludedRow['reason'], 'DISCOUNT_INVALID'>, string>
> = {
  NO_CHANGE: 'already has that value',
  PRICE_INVALID: 'the new price would be negative or too high',
  NO_BASE_PRICE:
    'its price is 0, so percent or amount changes do not apply: set its price instead',
  HIDDEN_EVERYWHERE:
    'it is off in every branch and this user cannot turn it on everywhere: someone who manages every branch must do it',
};

/**
 * A model-facing text of a proposal tool for the member's gate (plan F10
 * naming rule: a sentence naming another tool or op only while it is
 * offered). `null` = the tool's static definition, which is offered to no
 * one: the text without its gated sentences.
 */
export type TProposalText = (gate: IGpthelpToolGate | null) => string;

/**
 * What each write tool's description says while the other write tool is
 * offered too (plan F2.3, P5-3): a ready card ends the turn, so a request
 * with menu and inventory changes calls both tools in one round; and «se
 * acabó X» reads as sold out on the menu, not as a stock change (the S8
 * close: with both tools offered the model had started zeroing the stock of
 * «crema» instead of pausing the topping; a paid A/B chose this wording).
 * Names no tool (plan F10); each tool writes it only while the other is
 * offered.
 */
export const BOTH_TOOLS_SENTENCE =
  'When one request needs menu and inventory changes, call both tools in the same round. «Se acabó X» about something customers order is a menu change (pause it), not an inventory change, unless the user talks about stock amounts.';

/**
 * What the model reads about discounted prices, written for the ops offered
 * to the member (plan P1-1, F10 naming rule: a way to set a new discounted
 * price is named only while its op is offered).
 */
export interface IDiscountTexts {
  /** A price change needs the member's decision about a discount first. */
  decisionNote: (ops: readonly TAssistantOpName[]) => string;
  /** Why a product was left out as DISCOUNT_INVALID. */
  invalidWhy: (ops: readonly TAssistantOpName[]) => string;
  /** Why a new product was left out as DISCOUNT_INVALID: it is not created. */
  invalidCreateWhy: (ops: readonly TAssistantOpName[]) => string;
}

/** What makes one proposal kind's write tool (plan F10). */
export interface IProposalToolOptions {
  kind: TAssistantKind;
  /** The tool description: the global rules only (plan F11). */
  description: TProposalText;
  /** The `op` line of each op of the kind: its fields (plan F11). */
  opDocs: Readonly<Partial<Record<TAssistantOpName, TProposalText>>>;
  /**
   * What follows the op lines in `op` for the ops offered (plan P3-5: the
   * option groups' trailer); absent or null: nothing.
   */
  opTrailer?: (
    ops: readonly TAssistantOpName[],
    gate: IGpthelpToolGate | null
  ) => string | null;
  /**
   * The kind's texts about discounted prices (plan P1-1); absent for a kind
   * whose ops change no price (inventory, plan P5-1).
   */
  discountTexts?: IDiscountTexts;
}

/**
 * The definition that offers `ops`, with the fields of each in `op`: one line
 * per op, in enum order, then the trailer of those ops (plan F10).
 */
function definitionWith(
  name: string,
  { description, opDocs, opTrailer }: IProposalToolOptions,
  ops: readonly TAssistantOpName[],
  gate: IGpthelpToolGate | null
): IHostToolDefinition {
  const trailer = opTrailer?.(ops, gate) ?? null;
  const lines = ops.map((op) => opDocs[op](gate));
  return {
    name,
    description: description(gate),
    sideEffects: 'none',
    parameters: {
      type: 'object',
      additionalProperties: false,
      required: ['changes'],
      properties: {
        changes: {
          type: 'array',
          minItems: 1,
          maxItems: MENU_OP_LIMITS.changes,
          items: {
            type: 'object',
            additionalProperties: false,
            required: ['op'],
            properties: {
              op: {
                type: 'string',
                enum: [...ops],
                description: (trailer ? [...lines, trailer] : lines).join('\n'),
              },
              ...fieldSchemasOf(ops),
            },
          },
        },
      },
    },
  };
}

/**
 * A lock or refusal card, with «Marcar un producto como agotado» when hiding
 * is still possible; `note`: what the model reads (`LOCK_NOTE` for a lock).
 */
function lockEnvelope(
  card: IGhCalloutCard,
  reason: string,
  offersToggle: boolean,
  fmt: ICardFormat,
  note: string
): IGhToolResultEnvelope {
  return toolResultEnvelope({
    data: { status: 'refused', reason, note },
    cards: [card],
    followUps: offersToggle ? [fmt.t('assistant.suggest.soldOut')] : [],
  });
}

/**
 * The lock card of `op` (plan F10): the switch, the plan, every branch, or
 * the role; `kind`: the tool's (a switched-off op points at its page).
 */
function lockResult(
  lock: TAssistantLock,
  op: TAssistantOpName,
  kind: TAssistantKind,
  { gate, turn, fmt }: IGpthelpToolExecCtx
): IGhToolResultEnvelope {
  const id = toolCardId('lock', turn.requestId);
  switch (lock) {
    // Only this op is off: the tool is not offered while every write is.
    case 'disabled':
      return lockEnvelope(
        writesOffCard(id, 'op', kindPageKey(kind, gate), fmt, kind),
        'disabled',
        false,
        fmt,
        LOCK_NOTE
      );
    case 'plan':
      return lockEnvelope(
        planLockCard(
          {
            id,
            title: fmt.t(planLockKey(op)),
            text: '',
            canSeePlans: canOpenPage(gate, PLANS_TARGET),
          },
          fmt
        ),
        'plan',
        false,
        fmt,
        LOCK_NOTE
      );
    case 'allBranches':
      return lockEnvelope(
        permissionLockCard(
          {
            id,
            titleKey: permissionLockKey('allBranches', op),
            canToggle: false,
          },
          fmt
        ),
        'allBranches',
        false,
        fmt,
        LOCK_NOTE
      );
    // CatalogManage in this branch: nothing to offer instead (plan P5-3).
    case 'stock':
      return lockEnvelope(
        permissionLockCard(
          { id, titleKey: permissionLockKey('stock', op), canToggle: false },
          fmt
        ),
        'permission',
        false,
        fmt,
        LOCK_NOTE
      );
    case 'catalog':
    case 'edit': {
      const toggleKey = lockToggleKey(op, gate);
      return lockEnvelope(
        permissionLockCard(
          {
            id,
            titleKey: permissionLockKey(lock, op),
            canToggle: toggleKey !== null,
            ...(toggleKey ? { toggleKey } : {}),
          },
          fmt
        ),
        'permission',
        assistantCanToggle(gate),
        fmt,
        LOCK_NOTE
      );
    }
    default: {
      const unknownLock: never = lock;
      throw new Error(`Unknown lock: ${String(unknownLock)}`);
    }
  }
}

/**
 * Why a new option choice (plan P3-2) or a new size of a product (plan
 * P4-1) was left out as DISCOUNT_INVALID: its own discounted price is not
 * below its price, and it has no discount yet to keep or remove. Names no
 * op or tool (plan F10).
 */
export const NEW_CHOICE_DISCOUNT_WHY =
  'its discounted price is not below its price, so it was left out: ask for a lower discounted price, or add it without one';

/** A product left out, with why in English for the model. */
interface IExcludedWithWhy extends IAssistantExcludedRow {
  why?: string;
}

/** The whys of DISCOUNT_INVALID, for the ops offered to the member. */
interface IDiscountInvalidWhys {
  /** Of a product or size whose price changes. */
  price: string;
  /** Of a new product (key `new:{n}`): it is not created. */
  create: string;
}

/**
 * Why something was left out; undefined for a reason of a newer API, or a
 * discounted price of a kind without discount texts (`discountInvalidWhys`
 * null: its ops change no price).
 */
function whyOf(
  row: IAssistantExcludedRow,
  discountInvalidWhys: IDiscountInvalidWhys | null
): string | undefined {
  const { reason } = row;
  if (reason === 'DISCOUNT_INVALID') {
    if (isNewChoiceExclusion(row) || isNewSizeExclusion(row))
      return NEW_CHOICE_DISCOUNT_WHY;
    if (discountInvalidWhys === null) return undefined;
    return isNewProductExclusion(row)
      ? discountInvalidWhys.create
      : discountInvalidWhys.price;
  }
  return Object.prototype.hasOwnProperty.call(EXCLUDED_WHY, reason)
    ? EXCLUDED_WHY[reason]
    : undefined;
}

/** The products left out with why each was (a reason of a newer API: none). */
function withWhy(
  excluded: IAssistantExcludedRow[],
  discountInvalidWhys: IDiscountInvalidWhys | null
): IExcludedWithWhy[] {
  return excluded.map((row) => {
    const why = whyOf(row, discountInvalidWhys);
    return why === undefined ? row : { ...row, why };
  });
}

/** The ops whose products TOO_MANY_ROWS `products` caps (plan P3-1). */
const GROUP_PRODUCTS_OPS: readonly TAssistantOpName[] = [
  'createOptionGroup',
  'linkOptionGroup',
  'unlinkOptionGroup',
];

/**
 * The op TOO_MANY_ROWS `products` refused: the first of the call that names
 * more than `max` products (the Action API reads the ops in order and stops
 * at the first; `opsFromArgs` already dropped repeated ids, as it does).
 * Undefined when none does.
 */
function oversizedGroupOp(
  ops: readonly IAssistantOpDto[],
  max: number
): IAssistantOpDto | undefined {
  return ops.find(
    (op) =>
      GROUP_PRODUCTS_OPS.includes(op.op) && (op.itemIds?.length ?? 0) > max
  );
}

/**
 * What the model reads with a 422 TOO_MANY_ROWS of an option-group cap (plan
 * P3-1). `products`: one change names more products than it may hold; a
 * link or unlink is split into several changes of the same call, but a new
 * group has no id another change could name, so it is prepared with `max`
 * products and the rest get it once it is applied. `itemGroups`: one product
 * would hold more option groups than it may. `links`: more products would get
 * or lose option groups than one proposal holds. Null for the other caps,
 * whose details say it all. `ops`: the ops of the refused call. Names no op
 * or tool (plan F10).
 */
export function tooManyRowsNote(
  details: IAssistantTooManyRowsDetails,
  ops: readonly IAssistantOpDto[]
): string | null {
  const { max } = details;
  if (typeof details.products === 'number')
    return oversizedGroupOp(ops, max)?.op === 'createOptionGroup'
      ? `A new group goes on at most ${max} products in one change: say so in one short sentence, then prepare it with ${max} of them; the rest can get it with another change once this one is applied.`
      : `One change names ${details.products} products and holds at most ${max}: split it into several changes of at most ${max} products each, in this same call.`;
  if (typeof details.itemGroups === 'number')
    return `A product would hold ${details.itemGroups} option groups, and one product holds at most ${max}: say so in one short sentence.`;
  if (typeof details.links === 'number')
    return `Too many products in one change: at most ${max} products may get or lose option groups at once. Say so in one short sentence and offer to do it in parts.`;
  return null;
}

/** What the model reads about the products left out (capped). */
function excludedData(excluded: IExcludedWithWhy[]) {
  return excluded.length > EXCLUDED_MAX
    ? { excluded: excluded.slice(0, EXCLUDED_MAX), truncated: true }
    : { excluded };
}

/**
 * What the card says stays hidden after the change, and why (plan P0-3,
 * P2-4): products by `itemId`, categories by `categoryId`; context for
 * later turns (capped).
 */
function stillHiddenData(input: IProposalCardInput) {
  return input.view.rows
    .filter((row) => input.stillHidden[row.key]?.length)
    .slice(0, STILL_HIDDEN_MAX)
    .map((row) => {
      const reasons = input.stillHidden[row.key];
      return rowEntity(row) === 'category'
        ? { categoryId: row.categoryId, reasons }
        : { itemId: row.itemId, reasons };
    });
}

/**
 * The needs_decision clause when `discounts` lists only the first of the
 * targets that need a decision (the API lists 20; `discountCount` counts
 * them all).
 */
function unlistedDiscountsClause(listed: number, count: number): string {
  return `discounts lists ${listed} of ${count}: name those and say there are ${
    count - listed
  } more.`;
}

/**
 * A refused proposal: invalid ops come back to the model as data (it asks one
 * short question; a discounted price needs the member's decision first, with
 * no card and no end of the turn); refusals the member should read get their
 * card. `discountDecisionNote`: how to ask, for the ops offered to the member
 * (null for a kind whose ops change no price: such a refusal reads as any
 * other invalid op); `ops`: the ops of the refused call; `kind`: the tool's.
 */
function proposeRefused(
  error: unknown,
  { deps, gate, turn, fmt }: IGpthelpToolExecCtx,
  discountDecisionNote: string | null,
  ops: readonly IAssistantOpDto[],
  kind: TAssistantKind
): unknown {
  const body = readAssistantError(error);
  refreshAssistantConfigOnRefusal(body, deps);
  if (
    discountDecisionNote !== null &&
    body?.code === 'ASSISTANT_INVALID_OP' &&
    body.details?.reason === 'DISCOUNT_DECISION'
  ) {
    const { reason, opIndex, discounts, discountCount } = body.details;
    const listed = discounts?.length ?? 0;
    const unlisted =
      typeof discountCount === 'number' && discountCount > listed;
    return {
      status: 'needs_decision',
      reason,
      opIndex,
      discounts,
      ...(unlisted ? { discountCount } : {}),
      note: unlisted
        ? `${discountDecisionNote} ${unlistedDiscountsClause(
            listed,
            discountCount
          )}`
        : discountDecisionNote,
    };
  }
  if (body?.code === 'ASSISTANT_INVALID_OP')
    return {
      status: 'invalid',
      reason: body.details?.reason ?? 'INVALID_OP',
      details: body.details ?? null,
    };
  if (body?.code === 'ASSISTANT_TOO_MANY_ROWS') {
    const details = body.details ?? null;
    const note = details ? tooManyRowsNote(details, ops) : null;
    return {
      status: 'invalid',
      reason: 'TOO_MANY_ROWS',
      details,
      ...(note ? { note } : {}),
    };
  }
  const card = refusalCard(
    body,
    toolCardId('lock', turn.requestId),
    gate,
    fmt,
    kind
  );
  if (!card) return throwNormalizedApiError(error);
  const offersToggle =
    body?.code === 'ASSISTANT_FORBIDDEN' &&
    body.details?.need === 'catalogManage' &&
    assistantCanToggle(gate);
  return lockEnvelope(
    card,
    body?.code ?? 'refused',
    offersToggle,
    fmt,
    body && LOCK_CODES.includes(body.code) ? LOCK_NOTE : CARD_NOTE
  );
}

/**
 * The changed option groups and how many products show each one (plan
 * P3-4: context for later turns; the card names the products).
 */
function affectedProductsData(affected: readonly IAssistantAffectedGroup[]) {
  return affected.map((group) => ({
    optionGroupId: group.groupId,
    title: group.title,
    products: group.itemCount,
  }));
}

/**
 * The line the turn ends with (the model gets no round after it, plan F1):
 * how many products show a change of a shared option group (plan P3-4);
 * else «toque Aplicar» only for one change shown inline (a collapsed card
 * has no «Aplicar»), or how many changes to review.
 */
function endSentence(
  rowCount: number,
  inlineSingle: boolean,
  affectedProducts: number,
  fmt: ICardFormat
): string {
  if (affectedProducts >= 2)
    return fmt.tc('assistant.proposal.endShared', affectedProducts, {
      n: fmt.number(affectedProducts),
    });
  return inlineSingle
    ? fmt.t('assistant.proposal.endSingle')
    : fmt.tc('assistant.proposal.endBatch', rowCount);
}

/**
 * The proposal is ready: the diff card (and the products that show a change
 * of an option group, plan P3-4), and the turn ends with one line. A call
 * that prepared nothing shows why on a card, and the model says what to do
 * instead. `discountInvalidWhys`: the whys of DISCOUNT_INVALID, for the ops
 * offered to the member (`offered`; the still-hidden note names those the
 * member may use).
 */
async function awaitingReview(
  response: IAssistantProposeResponse,
  ctx: IGpthelpToolExecCtx,
  discountInvalidWhys: IDiscountInvalidWhys | null,
  offered: readonly TAssistantOpName[]
): Promise<unknown> {
  const { deps, scope, turn, fmt, gate } = ctx;
  const excluded = withWhy(response.excluded, discountInvalidWhys);
  const store = deps.assistantStore;
  const proposal = response.proposal;
  if (response.outcome === 'empty' || !proposal || !hasRowsToApply(proposal)) {
    // A same-turn merge that left nothing discarded the earlier proposal.
    if (response.supersededId) store.clearPending(response.supersededId);
    // A proposal without rows is nothing to apply: it never waits.
    if (proposal) {
      store.clearPending(proposal.id);
      if (proposal.status === 'proposed')
        await discardEmptyProposal(scope.restaurantId, proposal.id);
    }
    return toolResultEnvelope({
      data: {
        status: 'no_changes',
        ...excludedData(excluded),
        note: EMPTY_NOTE,
      },
      cards: [
        nothingToChangeCard(
          toolCardId('empty', turn.requestId),
          response.excluded,
          fmt
        ),
      ],
    });
  }

  store.markPending(
    proposal.id,
    proposal.rowCount,
    proposal.expiresAt,
    response.supersededId,
    buildScopeKey(scope)
  );
  const inlineSingle =
    proposalMode(proposal) === 'inline' && proposal.rowCount === 1;
  const input = proposalCardInput(deps, proposal, turn.userText);
  const stillHidden = stillHiddenData(input);
  const affected = proposal.affected ?? [];
  const affectedCard = affectedProductsCard(proposal.id, affected, fmt);
  // A fix is named only when this member's role and plan let them make it.
  const fixes = offered.filter((op) => lockOf({ op }, gate) === null);
  return toolResultEnvelope({
    data: {
      status: 'awaiting_review',
      proposalId: proposal.id,
      rows: proposal.rowCount,
      note:
        'The user reviews and applies it in the card. Do not list rows. One short sentence.',
      ...(excluded.length ? excludedData(excluded) : {}),
      ...(stillHidden.length
        ? {
            stillHidden,
            stillHiddenNote: stillHiddenNote(fixes, gate.canHideEverywhere),
          }
        : {}),
      ...(affected.length
        ? { affectedProducts: affectedProductsData(affected) }
        : {}),
    },
    cards: affectedCard
      ? [proposalCard(input, fmt), affectedCard]
      : [proposalCard(input, fmt)],
    endTurnAfter: endSentence(
      proposal.rowCount,
      inlineSingle,
      affectedProductCount(affected),
      fmt
    ),
  });
}

/**
 * The write tool of a proposal kind (plan F10, X4, Y1): prepares an inert
 * proposal through the Action API; the member applies it from the card.
 * Offered while writes are on for the store, to members whose role the kind
 * allows, with the kind's ops not switched off for them; the first op the
 * switch, the role or the plan stops answers with its lock card.
 */
export function createProposalTool(
  options: IProposalToolOptions
): IGpthelpTool {
  const { kind } = options;
  const { name } = PROPOSAL_KINDS[kind];
  // Every op of the kind, switched off or not: a switched-off op sent from a
  // stale schema answers with its own card.
  const toolOps = opsOfKind(kind);
  const undocumented = toolOps.filter((op) => !options.opDocs[op]);
  if (undocumented.length)
    throw new Error(
      `[gpthelp] ${name}: no op line for ${undocumented.join(', ')}`
    );

  return {
    definition: definitionWith(name, options, toolOps, null),
    definitionFor: (gate) =>
      definitionWith(name, options, kindOps(kind, gate), gate),
    policy: 'proposal',
    isAvailable: (gate) => proposalToolAvailable(kind, gate),
    async execute(args, ctx) {
      const { scope, gate, turn } = ctx;
      // What the model reads (an unknown op's INVALID_ARGS, the discount
      // texts) names only the ops it may send.
      const offered = kindOps(kind, gate);
      const ops = opsFromArgs(args, toolOps, offered);
      const { discountTexts } = options;
      // The first op the switch, the role or the plan stops shows its card
      // (the Action API enforces the same).
      for (const op of ops) {
        const lock = lockOf(op, gate);
        if (lock) return lockResult(lock, op.op, kind, ctx);
      }

      let response: IAssistantProposeResponse;
      try {
        response = await AssistantService.propose(scope.restaurantId, {
          schemaVersion: ASSISTANT_SCHEMA_VERSION,
          kind,
          branchId: scope.branchId,
          ops,
          source: {
            conversationId: turn.conversationId,
            turnId: turn.turnId,
            requestId: turn.requestId,
            tainted: turn.tainted,
            requestText: Array.from(turn.userText)
              .slice(0, REQUEST_TEXT_MAX)
              .join(''),
          },
        });
      } catch (error) {
        return proposeRefused(
          error,
          ctx,
          discountTexts ? discountTexts.decisionNote(offered) : null,
          ops,
          kind
        );
      }
      return awaitingReview(
        response,
        ctx,
        discountTexts
          ? {
              price: discountTexts.invalidWhy(offered),
              create: discountTexts.invalidCreateWhy(offered),
            }
          : null,
        offered
      );
    },
  };
}
